/** Timers — scheduled-event list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { Icon } from "@/components/ui/icon";
import type { Timer } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/app/StatusBadge";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { RowActions } from "../_shared/ListScaffold";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { formatDateTime } from "@/lib/format";

export function TimerList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteTimer } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Timer | null>(null);

  const columns = useMemo<ColumnDef<Timer, unknown>[]>(
    () => [
      { id: "name", accessorKey: "name", header: "Name" },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "schedule",
        accessorKey: "schedule",
        header: "Schedule",
        cell: ({ row }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {row.original.schedule}
          </code>
        ),
      },
      {
        id: "emitted_event_type",
        accessorKey: "emitted_event_type",
        header: "Emits",
        cell: ({ row }) => (
          <Badge variant="outline">{row.original.emitted_event_type}</Badge>
        ),
      },
      {
        id: "next_due_at",
        accessorKey: "next_due_at",
        header: "Next due",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatDateTime(row.original.next_due_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("timers", row.original.id) })}
            onEdit={() => go({ to: editUrl("timers", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Timer, HttpError>({
    columns,
    refineCoreProps: {
      resource: "timers",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "next_due_at", order: "asc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Timers"
        description="Scheduled triggers that emit timer.fired events for the rule engine."
        action={
          <Button onClick={() => go({ to: createUrl("timers") })}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            Timer
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search timers…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No timers configured yet."
        onRowClick={(t) => go({ to: showUrl("timers", t.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete timer?"
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.name}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteTimer({ resource: "timers", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

export default TimerList;
